import React, { useEffect, useState, useRef } from 'react';
import Hero from '../components/Hero';
import CategoryCard from '../components/CategoryCard';
import { motion, useInView, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { categoriesFallback } from '../data';
import { Check, ArrowRight, Star, Quote, X, ChevronLeft, ChevronRight, ExternalLink, Gift, Package, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';

const julegaverItems = [
  {
    title: 'Julegaveideer',
    link: 'https://viewer.joomag.com/julekatalog2026-final/0608795001782386923',
    icon: Gift
  },
  {
    title: 'Gavekasser',
    link: 'https://issuu.com/prchokolade/docs/pr-cho_julekatalog2026_gavekasser_kundegaver',
    icon: Package
  },
  {
    title: 'Gavekort',
    link: 'https://viewer.joomag.com/jg-plus-online-flyer-2026-27/0246785001745926941',
    icon: CreditCard
  }
];

function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    damping: 30,
    stiffness: 100,
  });
  const displayValue = useTransform(springValue, (latest) => 
    Math.floor(latest).toLocaleString() + suffix
  );

  useEffect(() => {
    if (isInView) {
      motionValue.set(value);
    }
  }, [isInView, value, motionValue]);

  return <motion.span ref={ref}>{displayValue}</motion.span>;
}

const testimonialsFallbackHome = [
  {
    id: '1',
    name: { da: 'Gilleleje Sejlklub' },
    contact_person: { da: 'Bente Billenstein' },
    text: { da: '<p>Gilleleje Sejlklub har haft HnPromotion.dk som leverandør (v/ Bente Billenstein) i gennem mere end 20 år, og vi har fuldt ud været meget tilfredse med både tøj og service, det har vore sejlervenner i sejlklubben også.</p>' },
    logo: ['https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/44a5a2d0-27f3-4713-83fb-7b8f6fa0882d.jpg'],
    rating: 5
  },
  {
    id: '3',
    name: { da: 'Craft Sportswear Danmark' },
    contact_person: { da: 'Lars Poulsen' },
    text: { da: '<p>Vi har samarbejdet med HnPromotion i en årrække. Deres professionelle tilgang til profilbeklædning og evne til altid at finde den rette løsning gør dem til en uundværlig partner for os i hverdagen. De forstår vigtigheden af detaljer og leverer altid til tiden.</p>' },
    logo: ['https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/bbb5ffed-8be5-4e59-9bc5-b964c19233fc.png'],
    rating: 5
  },
  {
    id: '5',
    name: { da: 'Texet Distribution' },
    contact_person: { da: 'Jan Jensen' },
    text: { da: '<p>Altid professionel rådgivning og tøj i absolut topklasse. Et sikkert valg hver gang.</p>' },
    logo: ['https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/440d8707-72a6-421b-a8f3-1bdfcaedb106.png'],
    rating: 5
  }
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(2);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  useEffect(() => {
    document.title = "HN Promotion | Arbejdstøj og profilbeklædning";
    
    // Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', "Kæmpe udvalg af profilbeklædning, arbejdstøj og sportstøj med logotryk og brodering til både voksne og børn");

    // Open Graph Title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', "HN Promotion | Arbejdstøj og profilbeklædning");

    // Open Graph Description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', "Kæmpe udvalg af profilbeklædning, arbejdstøj og sportstøj med logotryk og brodering til både voksne og børn");

    // Open Graph Image
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', "");
  }, []);

  const getPlainText = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  const handlePrev = () => {
    if (!selectedTestimonial || testimonials.length === 0) return;
    const currentIndex = testimonials.findIndex(t => t.id === selectedTestimonial.id);
    const prevIndex = (currentIndex - 1 + testimonials.length) % testimonials.length;
    setSelectedTestimonial(testimonials[prevIndex]);
  };

  const handleNext = () => {
    if (!selectedTestimonial || testimonials.length === 0) return;
    const currentIndex = testimonials.findIndex(t => t.id === selectedTestimonial.id);
    const nextIndex = (currentIndex + 1) % testimonials.length;
    setSelectedTestimonial(testimonials[nextIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedTestimonial) return;
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        setSelectedTestimonial(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTestimonial, testimonials]);

  useEffect(() => {
    if (selectedTestimonial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedTestimonial]);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/testimonials')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch testimonials');
        return res.json();
      })
      .then(data => {
        if (data && data.length > 0) {
          const normalized = data.map(item => ({
            id: item.id,
            name: typeof item.name === 'object' ? item.name : { da: item.name },
            contact_person: typeof item.contact_person === 'object' ? item.contact_person : { da: item.contact_person },
            text: typeof item.text === 'object' ? item.text : { da: item.text },
            logo: Array.isArray(item.logo) ? item.logo : (item.logo ? [item.logo] : []),
            rating: item.rating || 5
          }));
          // Make sure Gilleleje Sejlklub is always first or in the list
          const gskIndex = normalized.findIndex(item => item.name.da.includes('Gilleleje'));
          let sorted = [...normalized];
          if (gskIndex > 0) {
            const gsk = sorted.splice(gskIndex, 1)[0];
            sorted.unshift(gsk);
          }
          setTestimonials(sorted.slice(0, 3));
        } else {
          setTestimonials(testimonialsFallbackHome);
        }
        setTestimonialsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setTestimonials(testimonialsFallbackHome);
        setTestimonialsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/categories')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch categories');
        return res.json();
      })
      .then(data => {
        if (data && data.length > 0) {
          const merged = categoriesFallback.map(fallbackItem => {
            const cmsItem = data.find(item => {
              const itemSlug = typeof item.slug === 'object' ? item.slug.da : item.slug;
              return itemSlug === fallbackItem.slug.da;
            });
            if (cmsItem) {
              return {
                ...fallbackItem,
                ...cmsItem,
                title: typeof cmsItem.title === 'object' ? cmsItem.title : { da: cmsItem.title },
                description: typeof cmsItem.description === 'object' ? cmsItem.description : { da: cmsItem.description },
              };
            }
            return fallbackItem;
          });
          setCategories(merged);
        } else {
          setCategories(categoriesFallback);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setCategories(categoriesFallback);
        setLoading(false);
      });
  }, []);

  return (
    <div className="bg-white">
      <Hero 
        title={
          <div className="flex flex-col gap-1 sm:gap-2 text-left sm:text-center">
            <span className="text-sm sm:text-base md:text-lg font-semibold tracking-widest text-orange-500 uppercase block">Velkommen til:</span>
            <span className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight text-[#1e335e] block">HN PROMOTION</span>
            <span className="text-lg sm:text-2xl md:text-3xl font-bold tracking-wide text-slate-700 block uppercase mt-1">
              DIN B2B EKSPERT I BRANDING OG GAVER
            </span>
          </div>
        }
        subtitle={<>Vi leverer alt inden for arbejdstøj, sportstøj og firmagaver.<br />Besøg vores showroom og se det store udvalg efter aftale.</>}
        image="https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/97b445b1-2b17-4470-be0b-340e0049d2a9.jpeg"
        ctaText="Besøg Showroom"
        ctaLink="/showroom"
      />

      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-24 lg:px-8">
        <div className="grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-12 items-start">
          {/* Left side: Text */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <h2 className="text-2xl font-bold tracking-tight text-[#1e335e] sm:text-5xl uppercase">Vores Kategorier</h2>
            <div className="mt-6 space-y-4 text-lg leading-8 text-gray-600">
              <p>
                Hos HN Promotion har vi siden 2000 været din trofaste partner inden for firmabeklædning og reklameartikler. Vi ved, at stærk branding er nøglen to succes, og derfor tilbyder vi skræddersyede løsninger, der fremhæver din virksomheds identitet.
              </p>
              <p>
                Vores sortiment omfatter alt fra kvalitetsarbejdstøj og profilbeklædning med tryk eller broderi til reklameartikler med logo.
              </p>
              
              {isTextExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <p>
                    Vi tilbyder også specialproduktion af caps, t-shirts, hoodies, jakker, tasker, muleposer, multiscarves og huer, som alle kan designes efter dine specifikationer.
                  </p>
                  <p>
                    Vi er din go-to kilde for unikke julegaver og fleksible gavekort, der sikrer, at dine kunder og medarbejdere føler sig værdsat.
                  </p>
                  <p>
                    For at give dig en ekstraordinær oplevelse, inviterer vi dig til at booke tid i vores samarbejdspartners imponerende 2200 m2 store showroom i Skovlunde. Her kan du udforske vores produkter og få inspiration til, hvordan vi kan hjælpe med at løfte din brandprofil.
                  </p>
                  <p>
                    Vores dedikation til kvalitet og kundetilfredshed gør os til det oplagte valg for virksomheder, der ønsker at skille sig ud. Kontakt os i dag, og lad os sammen skabe løsninger, der gør en forskel for din virksomhed.
                  </p>
                </motion.div>
              )}

              <div className="pt-2">
                <button
                  onClick={() => setIsTextExpanded(!isTextExpanded)}
                  className="text-orange-500 hover:text-orange-600 font-semibold text-base flex items-center gap-1 transition-colors cursor-pointer uppercase tracking-wider"
                >
                  {isTextExpanded ? 'Læs mindre' : 'Læs mere'}
                  <span className="text-xs transition-transform duration-200 inline-block" style={{ transform: isTextExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    ▼
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Right side: Boxes */}
          <div className="lg:col-span-7">
            {error && !categories.length && (
              <div className="text-center text-red-500">
                Der skete en fejl ved indlæsning af kategorier.
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {loading ? (
                Array(2).fill(0).map((_, i) => (
                  <div key={i} className="h-80 animate-pulse bg-gray-100 rounded-none" />
                ))
              ) : (
                categories.slice(0, visibleCount).map((category) => (
                  <CategoryCard
                    key={category.id}
                    title={category.title.da}
                    description={category.description.da}
                    image={category.image[0]}
                    slug={category.slug.da}
                    compact={true}
                  />
                ))
              )}
            </div>

            {!loading && visibleCount < categories.length && (
              <div className="mt-8 flex justify-start">
                <button
                  onClick={() => setVisibleCount(prev => prev + 2)}
                  className="inline-flex items-center gap-x-2 rounded-none bg-orange-500 px-8 py-4 text-sm font-semibold text-white hover:bg-orange-600 transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-orange-200 cursor-pointer duration-200 uppercase"
                >
                  Se flere
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Julegaver Section */}
      <section id="julegaver" className="bg-slate-50 py-16 sm:py-24 border-t border-gray-100 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12 sm:mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-[#1e335e] sm:text-5xl uppercase">
              Julegaver
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {julegaverItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex"
                >
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col justify-between w-full bg-white p-8 sm:p-10 border border-gray-200 shadow-sm hover:shadow-xl hover:border-orange-500 transition-all duration-300 rounded-none text-left"
                  >
                    <div>
                      <div className="flex h-14 w-14 items-center justify-center bg-orange-500 text-white mb-6 group-hover:bg-[#1e335e] transition-colors duration-300">
                        <IconComponent className="h-7 w-7" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#1e335e] uppercase tracking-wide group-hover:text-orange-500 transition-colors duration-200">
                        {item.title}
                      </h3>
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-100">
                      <div className="inline-flex items-center justify-center gap-x-2 rounded-none bg-orange-500 px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-white group-hover:bg-orange-600 transition-all duration-200 w-full shadow-sm">
                        <span>Se katalog</span>
                        <ExternalLink className="h-4 w-4" />
                      </div>
                    </div>
                  </a>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust section */}
      <div className="bg-gray-50 py-12 sm:py-16 border-y border-gray-100 bg-gradient-to-l from-[#133b54]/10 to-transparent">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 text-center sm:grid-cols-3">
            <div className="flex flex-col gap-y-2">
              <dt className="text-sm font-medium leading-7 text-gray-600 uppercase tracking-wider">Års erfaring</dt>
              <dd className="order-first text-3xl font-bold tracking-tight text-[#1e335e] sm:text-5xl">
                <Counter value={25} suffix="+" />
              </dd>
            </div>
            <div className="flex flex-col gap-y-2">
              <dt className="text-sm font-medium leading-7 text-gray-600 uppercase tracking-wider">Glade kunder</dt>
              <dd className="order-first text-3xl font-bold tracking-tight text-[#1e335e] sm:text-5xl">
                <Counter value={1000} suffix="+" />
              </dd>
            </div>
            <div className="flex flex-col gap-y-2">
              <dt className="text-sm font-medium leading-7 text-gray-600 uppercase tracking-wider">Produkter</dt>
              <dd className="order-first text-3xl font-bold tracking-tight text-[#1e335e] sm:text-5xl">
                <Counter value={5000} suffix="+" />
              </dd>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-white py-16 sm:py-24 border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold tracking-widest text-orange-500 uppercase block mb-2">
              Troværdighed & Samarbejde
            </span>
            <h2 className="text-3xl font-bold tracking-tight text-[#1e335e] sm:text-5xl uppercase">
              Det siger vores kunder
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Vi sætter en ære i at levere den bedste service og kvalitet hver gang. Her er et udpluk af vores kunders oplevelser.
            </p>
          </div>

          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3 items-stretch">
            {testimonialsLoading ? (
              Array(3).fill(0).map((_, i) => (
                <div key={i} className="h-64 bg-gray-50 animate-pulse rounded-none border border-gray-100" />
              ))
            ) : (
              testimonials.map((testimonial, index) => {
                const hasLogo = testimonial.logo && testimonial.logo.length > 0;
                const isLong = getPlainText(testimonial.text.da).length > 300;
                return (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex flex-col justify-between bg-white p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow relative group"
                  >
                    <div>
                      {/* Quote Icon & Stars */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="text-orange-500/20 group-hover:text-orange-500/30 transition-colors">
                          <Quote className="h-8 w-8 transform -scale-x-100" />
                        </div>
                        <div className="flex gap-1">
                          {Array(5).fill(0).map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-4 w-4 ${
                                i < testimonial.rating 
                                  ? 'text-orange-500 fill-orange-500' 
                                  : 'text-gray-200'
                              }`} 
                            />
                          ))}
                        </div>
                      </div>

                      {/* Text */}
                      <div 
                        className={`text-gray-600 leading-relaxed text-sm prose prose-orange ${
                          isLong ? 'line-clamp-6 mb-2' : 'mb-8'
                        }`}
                        dangerouslySetInnerHTML={{ __html: testimonial.text.da }}
                      />

                      {isLong && (
                        <button 
                          onClick={() => setSelectedTestimonial(testimonial)}
                          className="text-orange-500 hover:text-orange-600 font-bold text-xs uppercase tracking-wider mb-6 inline-flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          Læs mere
                        </button>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-100 pt-6 mt-auto flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm">
                          {testimonial.name.da}
                        </h3>
                        {testimonial.contact_person && testimonial.contact_person.da && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            v/ {testimonial.contact_person.da}
                          </p>
                        )}
                      </div>
                      {hasLogo && (
                        <div className="flex-shrink-0 bg-gray-50 p-2 border border-gray-100 h-20 w-36 flex items-center justify-center">
                          <img 
                            src={testimonial.logo[0]} 
                            alt={`${testimonial.name.da} logo`} 
                            className="max-h-16 max-w-full object-contain transition-all duration-300"
                          />
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/anbefalinger"
              className="inline-flex items-center gap-x-2 rounded-none bg-[#1e335e] px-8 py-4 text-sm font-semibold text-white hover:bg-[#132342] transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-slate-200 duration-200 uppercase"
            >
              Se alle anbefalinger
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedTestimonial && (() => {
          const currentIndex = testimonials.findIndex(t => t.id === selectedTestimonial.id);
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedTestimonial(null)}
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
              />

              {/* Navigation Wrapper */}
              <div className="relative w-full max-w-2xl flex items-center justify-center z-10">
                {/* Desktop Left Button */}
                <button
                  onClick={handlePrev}
                  className="hidden md:flex absolute -left-16 bg-white hover:bg-orange-500 hover:text-white text-gray-700 p-3 rounded-full shadow-lg border border-gray-100 transition-all cursor-pointer items-center justify-center z-20 hover:scale-110 active:scale-95 duration-200"
                  aria-label="Forrige"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>

                {/* Modal Card */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  className="relative bg-white w-full p-8 sm:p-10 border border-gray-100 shadow-2xl max-h-[90vh] overflow-y-auto flex flex-col justify-between"
                >
                  {/* Close Button & Page Indicator */}
                  <div className="absolute top-4 right-4 flex items-center gap-3">
                    <span className="text-xs text-gray-400 font-semibold bg-gray-50 px-2.5 py-1 border border-gray-100">
                      {currentIndex + 1} / {testimonials.length}
                    </span>
                    <button
                      onClick={() => setSelectedTestimonial(null)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-50 rounded-full cursor-pointer"
                      aria-label="Luk"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div>
                    {/* Quote Icon & Rating */}
                    <div className="flex items-center justify-between mb-6 pr-24">
                      <div className="text-orange-500/20">
                        <Quote className="h-12 w-12 transform -scale-x-100" />
                      </div>
                      <div className="flex gap-1">
                        {Array(5).fill(0).map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-5 w-5 ${
                              i < selectedTestimonial.rating 
                                ? 'text-orange-500 fill-orange-500' 
                                : 'text-gray-200'
                            }`} 
                          />
                        ))}
                      </div>
                    </div>

                    {/* Full Testimonial Text */}
                    <div 
                      className="text-gray-600 leading-relaxed mb-8 text-base prose prose-orange max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedTestimonial.text.da }}
                    />
                  </div>

                  {/* Client Info & Logo */}
                  <div className="border-t border-gray-100 pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">
                        {selectedTestimonial.name.da}
                      </h3>
                      {selectedTestimonial.contact_person && selectedTestimonial.contact_person.da && (
                        <p className="text-sm text-gray-500 mt-0.5">
                          v/ {selectedTestimonial.contact_person.da}
                        </p>
                      )}
                    </div>
                    {selectedTestimonial.logo && selectedTestimonial.logo.length > 0 && (
                      <div className="flex-shrink-0 bg-gray-50 p-3 border border-gray-100 h-20 w-36 flex items-center justify-center">
                        <img 
                          src={selectedTestimonial.logo[0]} 
                          alt={`${selectedTestimonial.name.da} logo`} 
                          className="max-h-16 max-w-full object-contain"
                        />
                      </div>
                    )}
                  </div>

                  {/* Mobile Navigation controls */}
                  <div className="flex md:hidden items-center justify-between border-t border-gray-100 pt-4 mt-6">
                    <button
                      onClick={handlePrev}
                      className="flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
                    >
                      <ChevronLeft className="h-5 w-5" />
                      Forrige
                    </button>
                    <span className="text-xs text-gray-400 font-medium">
                      {currentIndex + 1} af {testimonials.length}
                    </span>
                    <button
                      onClick={handleNext}
                      className="flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
                    >
                      Næste
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </motion.div>

                {/* Desktop Right Button */}
                <button
                  onClick={handleNext}
                  className="hidden md:flex absolute -right-16 bg-white hover:bg-orange-500 hover:text-white text-gray-700 p-3 rounded-full shadow-lg border border-gray-100 transition-all cursor-pointer items-center justify-center z-20 hover:scale-110 active:scale-95 duration-200"
                  aria-label="Næste"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
            </div>
          );
        })()}
      </AnimatePresence>

      {/* Showroom Section */}
      <div className="overflow-hidden bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative h-80 sm:h-auto overflow-hidden"
          >
            <img
              src="https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/3dba5e27-5e2a-473a-9503-da25494c4a85.jpeg"
              alt="HN Promotion Showroom"
              className="absolute inset-0 block h-full w-full object-cover"
            />
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-start px-6 py-12 sm:py-16 lg:px-16 xl:px-24"
          >
            <div className="max-w-lg">
              <p className="mt-2 text-2xl font-bold tracking-tight text-[#1e335e] sm:text-4xl uppercase">Besøg vores Showroom</p>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Kom forbi vores showroom og se vores store udvalg af profilbeklædning, sportstøj og firmagaver. Her kan du mærke kvaliteten, prøve størrelser og få en uforpligtende snak om jeres næste projekt.
              </p>
              <dl className="mt-10 space-y-2 text-base leading-7 text-gray-600">
                {[
                  'Se og mærk kvaliteten før du bestiller',
                  'Prøv størrelser og pasformer',
                  'Få personlig rådgivning af vores eksperter',
                  'Inspiration til jeres næste firmagave'
                ].map((item) => (
                  <div key={item} className="relative pl-9">
                    <dt className="inline font-semibold text-gray-900">
                      <div className="absolute left-1 top-1 h-5 w-5 text-black">
                        <Check className="h-5 w-5" />
                      </div>
                    </dt>
                    <dd className="inline">{item}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-10">
                <a
                  href="/showroom"
                  className="rounded-none bg-orange-500 px-8 py-4 text-sm font-semibold text-white hover:bg-orange-600 transition-all transform hover:-translate-y-1 hover:shadow-lg duration-200 inline-block uppercase"
                >
                  Læs mere om showroomet
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Orange Showroom Banner */}
      <div className="bg-[#1e335e] text-white py-10 px-6 sm:py-14 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 opacity-10 flex items-center justify-end pointer-events-none">
          <img 
            src="https://assets.auxodev.dk/assets/aeb461c8-3829-4985-b2c9-ddf73ce34675/4047aa7b-1ad9-4a98-bc53-6e903e27df3e.png" 
            alt="Watermark" 
            className="h-full max-w-xs sm:max-w-md md:max-w-lg object-contain translate-x-10 sm:translate-x-0"
          />
        </div>
        <div className="mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl uppercase">
            Besøg vores showroom i Skovlunde
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-white/90">
            Vi står klar til at tage imod dig og vise dig vores store udvalg af profilbeklædning, sportstøj og firmagaver. Bemærk: Showroomet kan kun besøges efter aftale.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/showroom"
              className="w-full sm:w-auto rounded-none bg-orange-500 px-8 py-4 text-sm font-semibold text-white shadow-lg hover:bg-orange-600 transition-all transform hover:-translate-y-1 hover:shadow-xl duration-200 text-center cursor-pointer uppercase"
            >
              Se adresse & book tid
            </a>
            <a 
              href="/kontakt" 
              className="w-full sm:w-auto rounded-none border border-white px-8 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-all transform hover:-translate-y-1 duration-200 text-center cursor-pointer uppercase"
            >
              Kontakt os i dag
            </a>
          </div>
        </div>
      </div>

    </div>
  );
}
